// store/listings.js
import { defineStore } from 'pinia';
import sanctum from '@api/auth/sanctum';
import userAPI from '@api/user';
import meAPI from '@api/me';
import navigation from '@router/navigation';
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { updateObi } from 'obi-loader';

import { useNotificationsStore } from './notifications';
import { useJobCountsStore } from './jobCounts';
export const useUserStore = defineStore('user', {
    state: () => {
        return {
            loading: false,
            data: null,
            authenticated: false,
            search: null,
            navigation: [],
            bearer: useStorage('bearer', null),
            tenantId: useStorage('tenantId', null),
            tenantSlug: useStorage('tenantSlug', null),
            tenantModalStatus: false,
            isMoveproAdmin: false,
            activity_status: useStorage('activity_status', null),
        };
    },
    actions: {
        async login(username, password) {
            try {
                this.loading = true;

                // reset active tenant on login
                this.tenantId = null;

                const { token } = await sanctum.login({
                    email: username,
                    password,
                });

                this.bearer = token;

                const user = await this.fetchUser();

                this.loading = false;

                return user;
            } catch (error) {
                this.authenticated = false;
                this.data = null;
                this.loading = false;

                // let the form component display the error
                throw error;
            }
        },

        async retrieve(userId) {
            return await userAPI.retrieve(userId);
        },

        async fetchUser(fetchNotifications = true) {
            try {
                this.loading = true;

                const { data } = await sanctum.me();

                this.data = data;

                if (data.tenants.length > 0) {
                    // if a tenant has already been set, use it if it exists for this user
                    if (this.tenantId) {
                        const validatedTenant = this.data.tenants.find(
                            (tenant) => tenant.id === parseInt(this.tenantId),
                        );

                        if (validatedTenant) {
                            await this.setTenant(validatedTenant.id, fetchNotifications);
                        } else {
                            await this.setTenant(data.tenants[0].id, fetchNotifications);
                        }
                    } else {
                        // otherwise, use the first tenant available for this user as the default
                        await this.setTenant(data.tenants[0].id, fetchNotifications);
                    }
                }

                this.activity_status = data.activity_status;
                this.authenticated = true;
                this.loading = false;

                return data;
            } catch {
                this.authenticated = false;
                this.data = null;
                this.loading = false;

                if (this.router && this.router.currentRoute.value.name !== 'Register') {
                    this.router.push('login');
                }
            }
        },

        async logout(silent = false) {
            try {
                this.loading = true;

                await sanctum.logout(silent);

                if (import.meta.env.VITE_ENV == 'production') {
                    Sentry.setUser(null);
                }

                this.data = null;
                this.authenticated = false;
                this.jwt = null;
                this.loading = false;

                // reset active tenant on logout
                this.tenantId = null;
                this.tenantSlug = null;
            } catch (error) {
                this.data = null;
                this.authenticated = false;
                this.loading = false;

                throw error;
            } finally {
                this.loading = false;
            }
        },

        async update(userData) {
            try {
                const { data } = await userAPI.update(this.data.id, userData);
                this.data = data.user;

                toast.success('User updated successfully');

                return data.user;
            } catch (error) {
                toast.error('User update failed');

                throw error;
            }
        },

        async requestResetPassword(email) {
            const { data } = await userAPI.requestResetPassword(email);

            if (data) {
                return true;
            }
        },

        async confirmResetPassword(token, email, password, password_confirmation) {
            const { data } = await userAPI.confirmResetPassword(token, email, password, password_confirmation);

            if (data) {
                return true;
            }
        },

        async setTenant(tenantId, fetchNotifications = true) {
            const tenant = this.data.tenants.find((tenant) => tenant.id === tenantId);

            if (tenant) {
                try {
                    await meAPI.setActiveTenant(tenantId);
                } catch (error) {
                    // If API call fails, still set local state but log the error
                    console.error('Failed to set active tenant in session:', error);
                    throw error;
                }

                this.tenantId = tenant.id;
                this.tenantSlug = tenant.slug;
                this.isMoveproAdmin = this.tenantId == 1000;
            }

            if (import.meta.env.VITE_ENV == 'production') {
                Sentry.setUser({
                    id: this.data.id,
                    name: this.data.fullname,
                    email: this.data.email,
                    tenant_id: this.tenantId,
                    tenant_slug: this.tenantSlug,
                });
            }

            if (import.meta.env.VITE_ENV == 'development') {
                updateObi({
                    position: 'bottom-right',
                    user: {
                        id: this.data.id,
                        email: this.data.email,
                        first_name: this.data.first_name,
                        last_name: this.data.last_name,
                        company: this.data.company.name,
                    },
                });
            }

            // this.navigation = navigation.populateNavigation(this.isTenantContext, [...this.data.roles]);
            // Initial navigation build - will be updated by watcher
            this.rebuildNavigation(false); // Start with desktop view

            if (fetchNotifications) {
                const notifications = useNotificationsStore();
                const jobCounts = useJobCountsStore();
                await notifications.retrieve();
                await jobCounts.fetchCounts();

                if (window.echo) {
                    window.echo.leaveAllChannels();

                    notifications.startListener();
                    jobCounts.startListener();
                } else {
                    console.log('Echo not available for WebSocket setup');
                }
            }
        },

        async showTenantModal() {
            await this.fetchUser(false);
            this.tenantModalStatus = true;
        },

        hideTenantModal() {
            this.tenantModalStatus = false;
        },

        toggleTenantModal() {
            this.tenantModalStatus = this.tenantModalStatus ? false : true;
        },

        rebuildNavigation(isMobile) {
            if (this.data && this.data.roles) {
                this.navigation = navigation.populateNavigation(this.isTenantContext, [...this.data.roles], isMobile);
            }
        },
    },
    getters: {
        companyHasStripeAccount: (state) => !!state.data?.company?.settings?.stripe_id,
        isAuthenticated: (state) => {
            return state.authenticated;
        },

        isCentralContext(state) {
            return state.data?.company_id === 1000 && state.tenantId === 1000 && state.tenantSlug === 'movepro';
        },

        isTenantContext() {
            return this.isCentralContext === false;
        },

        hasMultipleTenants(state) {
            return state.data?.company_id === 1000 && state.data?.tenants.length > 1;
        },

        isRemovalist(state) {
            if (state.data && state.data.roles && state.data.roles.includes('MANAGER')) {
                return true;
            } else {
                return false;
            }
        },

        isAdmin(state) {
            if (state.data && state.data.roles && state.data.roles.includes('ADMIN')) {
                return true;
            } else {
                return false;
            }
        },

        isOwner(state) {
            if (state.data && state.data.roles && state.data.roles.includes('OWNER')) {
                return true;
            } else {
                return false;
            }
        },

        isFinance(state) {
            if (state.data && state.data.roles && state.data.roles.includes('FINANCE')) {
                return true;
            } else {
                return false;
            }
        },

        isAgent(state) {
            if (state.data && state.data.roles && state.data.roles.includes('SALES')) {
                return true;
            } else {
                return false;
            }
        },

        isTriage(state) {
            if (state.data && state.data.roles && state.data.roles.includes('OPS')) {
                return true;
            } else {
                return false;
            }
        },

        isAdminOrSuper(state) {
            if (
                state.data &&
                state.data.roles &&
                (state.data.roles.includes('ADMIN') || state.data.roles.includes('SUPER'))
            ) {
                return true;
            }
            return false;
        },

        isOwnerOrSuper(state) {
            if (
                state.data &&
                state.data.roles &&
                (state.data.roles.includes('OWNER') || state.data.roles.includes('SUPER'))
            ) {
                return true;
            }
            return false;
        },

        isManager(state) {
            return state.data?.roles?.includes('MANAGER') ?? false;
        },

        isSuper(state) {
            if (state.data && state.data.roles && state.data.roles.includes('SUPER')) {
                return true;
            } else {
                return false;
            }
        },

        isDev(state) {
            if (state.data && state.data.roles && state.data.roles.includes('DEVELOPER')) {
                return true;
            } else {
                return false;
            }
        },

        // only applies when you're in the Movepro tenant view (i.e. when the active tenant is Movepro)
        isMoveproSuperAdminView(state) {
            return state.isMoveproAdmin && state.hasAnyRole(['SUPER', 'DEVELOPER']);
        },

        // applies regardless of tenant context (i.e. when you're a Movepro user in any tenant)
        isMoveproSuperAdminUser(state) {
            return state.data?.company_id === 1000;
        },

        // we should migrate usage of this to isMoveproSuperAdminView and remove this getter
        // we also need to audit use of this and .isMoveproAdmin in the codebase as it's ambiguous
        // and potentially used incorrectly or with incorrect assumptions about when it's truthy
        isMoveproSuperAdmin(state) {
            return state.isMoveproSuperAdminView;
        },

        companyId(state) {
            return state.data?.company_id;
        },

        isOnboarded(state) {
            if (state.data && state.data.roles) {
                if (state.data.roles.includes('OWNER') || state.data.roles.includes('ADMIN')) {
                    return true;
                }
            }

            return false;
        },

        getUser(state) {
            return state.data;
        },

        getUserId(state) {
            return state.data?.id;
        },

        getTenant(state) {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === state.tenantId);
            }

            return null;
        },

        getTimezone(state) {
            return state.data?.timezone;
        },

        getAvatar(state) {
            return state.data?.avatar;
        },

        getFullName(state) {
            if (state.data && state.data.fullname) {
                return state.data.fullname;
            }
        },

        getHourlyRate(state) {
            return state.data?.hourly_rate ?? 0;
        },

        getInitials(state) {
            if (state.data) {
                const firstName = state.data.first_name || '';
                const lastName = state.data.last_name || '';

                const firstInitial = firstName.charAt(0).toUpperCase();
                const lastInitial = lastName.charAt(0).toUpperCase();

                return `${firstInitial}${lastInitial}`;
            }
            return '';
        },

        getFirstName(state) {
            if (state.data && state.data.first_name) {
                return state.data.first_name;
            }
        },

        getLastName(state) {
            if (state.data && state.data.last_name) {
                return state.data.last_name;
            }
        },

        getUsername(state) {
            if (state.data) {
                return state.data.username;
            }
        },

        getRoles(state) {
            if (state.data) {
                return state.data.roles;
            }
        },

        getCompanyId(state) {
            if (state.data) {
                return state.data.company_id;
            }
        },

        getTeam(state) {
            return state.data?.team;
        },

        // gates
        hasRole: (state) => {
            return (role) => {
                if (state.data && state.data.roles) {
                    return state.data.roles.includes(role);
                }
            };
        },

        unlessRole: (state) => {
            return (role) => {
                if (state.data && state.data.roles) {
                    return !state.data.roles.includes(role);
                }
            };
        },

        hasAnyRole: (state) => {
            return (roles, alwaysAllowOwnerAndAdmin = true) => {
                if (state.data && Array.isArray(state.data.roles)) {
                    if (
                        alwaysAllowOwnerAndAdmin &&
                        (state.data.roles.includes('OWNER') || state.data.roles.includes('ADMIN'))
                    ) {
                        return true;
                    }

                    const roleList = Array.isArray(roles) ? roles : [roles];
                    return roleList.some((r) => state.data.roles.includes(r));
                }

                // If we can't even read state.data.roles, deny by default:
                return false;
            };
        },

        hasEveryRole: (state) => {
            return (roles) => {
                if (state.data && state.data.roles) {
                    // Admin roles have access to everything
                    if (state.data.roles.includes('OWNER') || state.data.roles.includes('ADMIN')) {
                        return true;
                    }
                    return roles.every((role) => state.data.roles.includes(role));
                }
            };
        },

        // Helper method to check if user has FIELD_STAFF role but is restricted (no OWNER/ADMIN/MANAGER/OPS/SALES)
        isRestrictedFieldStaff: (state) => {
            return () => {
                if (state.data && state.data.roles) {
                    return (
                        state.data.roles.includes('FIELD_STAFF') &&
                        !state.data.roles.includes('OWNER') &&
                        !state.data.roles.includes('ADMIN') &&
                        !state.data.roles.includes('MANAGER') &&
                        !state.data.roles.includes('OPS') &&
                        !state.data.roles.includes('SALES')
                    );
                }

                return false;
            };
        },

        // Helper method to check if user has only FIELD_STAFF role
        isOnlyFieldStaff: (state) => {
            return () => {
                if (state.data && state.data.roles) {
                    return state.data.roles.includes('FIELD_STAFF') && state.data.roles.length === 1;
                }

                return false;
            };
        },

        // Helper method to check if user should see count badges in navigation
        shouldShowCountBadges: (state) => {
            return () => {
                if (!state.data || !state.data.roles) {
                    return false;
                }

                // Show counts only for roles that have access to orders/leads
                return state.data.roles.some((role) =>
                    ['SUPER', 'ADMIN', 'OWNER', 'MANAGER', 'FINANCE', 'SALES', 'OPS'].includes(role),
                );
            };
        },

        getBaseAccent: () => {
            return '#1F3DF0';
        },

        getTenantAccent: (state) => {
            if (state.tenantId) {
                return state.data?.tenants?.find((tenant) => tenant.id === state.tenantId)?.accent;
            }

            return '#1F3DF0';
        },

        getTenantLogo: () => {
            return 'https://movepro-assets.s3.ap-southeast-2.amazonaws.com/tenants/movepro/movepro-logo.svg';
        },

        hasAuth() {
            return (rolesToCheck) => {
                return this.hasAnyRole(rolesToCheck, true);
            };
        },
    },
});
